/**
 * UUID v4 generator with graceful fallback.
 *
 * `crypto.randomUUID()` is only available in secure contexts (HTTPS or
 * localhost). Behind a Tailscale IP — which the browser does not treat
 * as secure — the call throws "crypto.randomUUID is not a function".
 *
 * We try the native call first, then `crypto.getRandomValues` (which is
 * available in more contexts), then fall back to `Math.random()` for
 * truly stuck environments. The fallback is not cryptographically
 * secure but is fine for the chat conversation id, which is just a
 * client-side opaque key.
 */
export function uuidv4(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    try {
      return crypto.randomUUID()
    } catch {
      // fall through to next strategy
    }
  }

  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = new Uint8Array(16)
    crypto.getRandomValues(bytes)
    // RFC 4122 §4.4: set version (4) and variant (10xx) bits.
    bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40
    bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80
    return formatUuid(bytes)
  }

  const bytes = new Uint8Array(16)
  for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256)
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80
  return formatUuid(bytes)
}

function formatUuid(bytes: Uint8Array): string {
  const hex: string[] = []
  for (let i = 0; i < 16; i++) {
    hex.push((bytes[i] ?? 0).toString(16).padStart(2, '0'))
  }
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`
}
